import { useController, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FillInTheBlankFormFields } from '@/components/question/forms/fill-in-the-blank/FillInTheBlankForm';
import {
  IsNewItemArgs,
  UseQuestionFieldArrayReturn,
} from '@/data/hook/question/answers/types';
import { useSpotlight } from '@/data/hook/shared/useSpotlight';
import { FillInTheBlankOptionCreateDTO } from '@/models/Answer';
import { validateFillInTheBlankOptions } from '@/utils/questions/validateAnswers';

type UseFillInTheBlankOptionsArgs = {
  blankCount: number;
};

type UseFillInTheBlankOptionsReturn =
  UseQuestionFieldArrayReturn<FillInTheBlankOptionCreateDTO> & {
    handleUpdateValidPositions: (
      index: number,
      validPositions: number[],
    ) => void;
    handleMarkIncorrect: (index: number) => void;
  };

export function useFillInTheBlankOptions({
  blankCount,
}: UseFillInTheBlankOptionsArgs): UseFillInTheBlankOptionsReturn {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });
  const { t: tFillInTheBlank } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.fillInTheBlankForm',
  });

  const { control, getValues, setValue } =
    useFormContext<FillInTheBlankFormFields>();
  const {
    fieldState: { error },
  } = useController({ control, name: 'options' });

  const { focus, clearIfMatches, isFocused } = useSpotlight();

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'options',
    keyName: 'fieldId',
    rules: {
      validate: (options = []) =>
        validateFillInTheBlankOptions(options, blankCount, t, tFillInTheBlank),
    },
  });

  const handleAdd = () => {
    append({ validPositions: [] });
    focus(fields.length);
  };

  const handleUpdateText = (index: number, text: string) => {
    const current = getValues(`options.${index}`);
    update(index, { ...current, text, mediaFile: null, altText: '' });
    clearIfMatches(index);
  };

  const handleUpdateMedia = (
    index: number,
    mediaFile: File | string,
    altText?: string,
  ) => {
    const current = getValues(`options.${index}`);
    update(index, {
      ...current,
      mediaFile,
      altText: altText ?? null,
      text: '',
    });
    clearIfMatches(index);
  };

  const handleUpdateValidPositions = (
    index: number,
    validPositions: number[],
  ) => {
    setValue(`options.${index}.validPositions`, validPositions);
    clearIfMatches(index);
  };

  const handleMarkIncorrect = (index: number) => {
    setValue(`options.${index}.validPositions`, []);
    clearIfMatches(index);
  };

  const handleDelete = (index: number) => {
    remove(index);
    clearIfMatches(index);
  };

  const isNewItem = ({ index }: IsNewItemArgs) => isFocused(index);

  return {
    fields,
    error,
    handleAdd,
    handleUpdateText,
    handleUpdateMedia,
    handleUpdateValidPositions,
    handleMarkIncorrect,
    handleDelete,
    isNewItem,
  };
}
